import { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/auth.service';
import databaseService from '../services/database.service';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    checkSession();
  }, []);
  
  const checkSession = async () => {
    try {
      setIsLoading(true);
      
      // Check if user has existing session
      if (authService.isAuthenticated()) {
        const user = authService.getCurrentUser();
        const couchdbCreds = authService.getCouchDBCredentialsFromStorage();
        
        // Create session object compatible with existing code
        setSession({
          userId: user.id,
          accountId: user.id, // For MVP, use userId as accountId
          user: user,
          couchdbCredentials: couchdbCreds,
        });
        
        // Initialize database if credentials are available
        if (couchdbCreds && couchdbCreds.databaseUrl) {
          console.log('[AuthContext] Reinitializing database sync from stored credentials...');
          try {
            await databaseService.initialize({
              couchdbUrl: couchdbCreds.databaseUrl,
              couchdbUsername: couchdbCreds.username,
              couchdbPassword: couchdbCreds.password,
              accountId: user.id,
              userId: user.id,
            });
            console.log('[AuthContext] Database sync reinitialized successfully');
          } catch (dbError) {
            console.error('[AuthContext] Failed to reinitialize database:', dbError);
            // Don't fail the session check if database init fails
          }
        }
      } else {
        setSession(null);
      }
    } catch (err) {
      console.error('Session check failed:', err);
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  };
  
  const login = async (email, password) => {
    try {
      setError(null);
      setIsLoading(true);
      
      // Call AuthService login
      const result = await authService.login(email, password);
      
      // Create session object
      const newSession = {
        userId: result.user.id,
        accountId: result.user.id, // For MVP, use userId as accountId
        user: result.user,
        couchdbCredentials: {
          databaseUrl: result.couchdbUrl,
          username: result.couchdbUsername,
          password: result.couchdbPassword,
        },
      };
      
      setSession(newSession);
      
      // Initialize PouchDB sync with CouchDB credentials
      console.log('[AuthContext] Initializing database sync...');
      await databaseService.initialize({
        couchdbUrl: result.couchdbUrl,
        couchdbUsername: result.couchdbUsername,
        couchdbPassword: result.couchdbPassword,
        accountId: result.user.id,
        userId: result.user.id,
      });
      console.log('[AuthContext] Database sync initialized successfully');
      
      return {
        session: newSession,
        requiresAccountSelection: false, // MVP: single account
      };
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithPin = async (email, pin) => {
    try {
      setError(null);
      // PIN login not supported in web app MVP
      throw new Error('PIN login is only available in the desktop app');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const setupPin = async (pin, confirmPin) => {
    try {
      setError(null);
      // PIN setup not supported in web app MVP
      throw new Error('PIN setup is only available in the desktop app');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const getPinStatus = async () => {
    try {
      setError(null);
      // PIN not supported in web app MVP
      return { hasPinEnabled: false };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const disablePin = async (password) => {
    try {
      setError(null);
      // PIN not supported in web app MVP
      throw new Error('PIN is only available in the desktop app');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateLoginPreference = async (preferredMethod, rememberChoice) => {
    try {
      setError(null);
      // Login preferences not supported in web app MVP
      console.warn('Login preferences not supported in web app');
      return { success: true };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const getLoginPreferences = async (email) => {
    try {
      // Always return password method for web app
      return { preferredMethod: 'password', rememberChoice: false };
    } catch (err) {
      console.error('Get login preferences failed:', err);
      return { preferredMethod: 'password', rememberChoice: false };
    }
  };
  
  const register = async (fullName, email, password, licenseKey) => {
    try {
      setError(null);
      // Registration not implemented in web app MVP
      throw new Error('Registration is not yet available in the web app. Please use the desktop app to register.');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  const createAccount = async (userId, businessName, country, currency, taxSystem) => {
    try {
      setError(null);
      // Account creation not implemented in web app MVP
      throw new Error('Account creation is not yet available in the web app. Please use the desktop app.');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  const selectAccount = async (accountId) => {
    try {
      setError(null);
      // Account selection not needed in web app MVP (single account)
      console.warn('Account selection not needed in web app MVP');
      return session;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  const switchAccount = async (accountId) => {
    try {
      setError(null);
      // Account switching not needed in web app MVP (single account)
      console.warn('Account switching not needed in web app MVP');
      return session;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  const logout = async () => {
    try {
      // Cleanup database sync
      console.log('[AuthContext] Cleaning up database sync...');
      await databaseService.cleanup();
      
      // Call AuthService logout
      await authService.logout();
      setSession(null);
      
      // Force page reload to clear all cached state
      window.location.href = '/login';
    } catch (err) {
      console.error('Logout failed:', err);
      // Still redirect even if logout fails
      window.location.href = '/login';
    }
  };
  
  const value = {
    session,
    isLoading,
    error,
    isAuthenticated: !!session,
    hasAccount: !!session?.accountId,
    login,
    loginWithPin,
    setupPin,
    getPinStatus,
    disablePin,
    updateLoginPreference,
    getLoginPreferences,
    register,
    createAccount,
    selectAccount,
    switchAccount,
    logout,
    checkSession, // Expose checkSession for manual refresh
  };
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
